import React, { useEffect, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from "chart.js";
import "./Posture.css";

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

export default function Posture() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalScore, setGlobalScore] = useState(0);
  const [timeRange, setTimeRange] = useState(30);

  const countryToFlag = (code) => {
    if (!code) return "https://flagcdn.com/w80/un.png";
    return `https://flagcdn.com/w80/${code.toLowerCase()}.png`;
  };

  const generatePDF = async () => {
    document.body.classList.add("pdf-exporting");
    await new Promise((r) => setTimeout(r, 100));

    const input = document.getElementById("posture-report");
    const logoUrl = "/img/logo_cti.png";

    if (!input) {
      alert("Missing content to export.");
      document.body.classList.remove("pdf-exporting");
      return;
    }

    input.querySelectorAll("img").forEach((img) =>
      img.setAttribute("crossorigin", "anonymous")
    );

    const logoBase64 = await fetch(logoUrl)
      .then((r) => r.blob())
      .then(
        (blob) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          })
      );

    const canvas = await html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#1e1e1e",
      allowTaint: false,
      foreignObjectRendering: false,
      logging: false,
    });

    const imgData = canvas.toDataURL("image/jpeg", 0.85);
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const marginX = 10;
    const imgProps = pdf.getImageProperties(imgData);
    const imgWidth = pageWidth - marginX * 2;
    const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

    pdf.setFillColor(30, 30, 30);
    pdf.rect(0, 0, pageWidth, 300, "F");

    if (logoBase64) {
      pdf.addImage(logoBase64, "JPEG", marginX, 8, 30, 12);
    }

    pdf.setFontSize(16);
    pdf.setTextColor("#8c7ae6");
    pdf.text("Circet - Global Security Posture", 45, 16);

    const periodText =
      "Period: " +
      (timeRange === 7
        ? "Last 7 days"
        : timeRange === 30
        ? "Last 30 days"
        : "Last 90 days");
    pdf.setFontSize(12);
    pdf.text(periodText, pdf.internal.pageSize.getWidth() - 60, 16);

    pdf.addImage(imgData, "JPEG", marginX, 25, imgWidth, imgHeight);

    const disclaimer = "© 2025 CIRCET CTI – Confidential – Internal Use Only";
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(9);
    pdf.setTextColor("#666");
    const discW = pdf.getTextWidth(disclaimer);
    pdf.text(disclaimer, (pageWidth - discW) / 2, pdf.internal.pageSize.getHeight() - 10);

    const iso = new Date().toISOString().split("T")[0];
    pdf.save(`posture_report_${iso}.pdf`);

    document.body.classList.remove("pdf-exporting");
  };

  useEffect(() => {
    setLoading(true);
    fetch(`/api/stats_posture.php?time_range=${timeRange}`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((json) => {
        if (json.success) {
          setData(json.data);
          const avg =
            json.data.reduce((acc, c) => acc + c.score, 0) / json.data.length;
          setGlobalScore(Math.round(avg * 100));
        }
      })
      .catch((err) => {
        console.error("Failed to load posture data", err);
        setData([]);
      })
      .finally(() => setLoading(false));
  }, [timeRange]);

  if (loading) {
    return (
      <div className="profile-wrapper text-center text-light">
        <h4>Loading Security Posture...</h4>
      </div>
    );
  }

  return (
    <div className="profile-wrapper">
      <div className="cti-filters mb-4">
        <div className="cti-filters-inner">
          <span className="cti-title">Security Posture Overview</span>
          <div className="d-flex align-items-center gap-2">
            <select
              className="cti-select"
              value={timeRange}
              onChange={(e) => setTimeRange(parseInt(e.target.value))}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button className="btn-export" onClick={generatePDF}>
              Export PDF
            </button>
          </div>
        </div>
      </div>

      <div id="posture-report">
        <div className="posture-global text-center mb-4">
          <i className="bi bi-shield-shaded posture-global-icon"></i>
          <div className="posture-global-score">{globalScore}%</div>
          <div className="posture-global-label">Global Average – Circet</div>
        </div>

        <div className="posture-grid">
          {data.map((c, idx) => (
            <div className="posture-card" key={idx}>
              <div className="posture-card-header">
                <img
                  src={countryToFlag(c.country_code)}
                  alt={c.country_code}
                  className="posture-flag"
                  crossOrigin="anonymous"
                />
                <span className="posture-country">{c.country_code}</span>
              </div>

              {c.domain_scores && (
                <div className="posture-radar-wrapper">
                  <div className="posture-radar">
                    <Radar
                      data={{
                        labels: Object.keys(c.domain_scores),
                        datasets: [
                          {
                            label: "Maturity",
                            data: Object.values(c.domain_scores),
                            backgroundColor: "rgba(52,152,219,0.15)",
                            borderColor: "#3498db",
                            pointBackgroundColor: "#3498db",
                            borderWidth: 2,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        aspectRatio: 1.3,
                        layout: {
                          padding: {
                            top: 30,
                            bottom: 30,
                            left: 40,
                            right: 40,
                          },
                        },
                        clip: false,
                        scales: {
                          r: {
                            angleLines: { color: "#8c7ae6" },
                            grid: { color: "#8c7ae6" },
                            pointLabels: {
                              color: "#f8f9fa",
                              font: { size: 12 },
                              padding: 18,
                            },
                            ticks: {
                              color: "#ccc",
                              backdropColor: "transparent",
                              max: 100,
                              min: 0,
                              stepSize: 20,
                              showLabelBackdrop: false,
                            },
                          },
                        },
                        plugins: {
                          legend: { display: false },
                        },
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="posture-metrics">
                <div className="metric">
                  <span className="label">Policies</span>
                  <span className="value">{c.policy_pct}%</span>
                </div>
                <div className="metric">
                  <span className="label">Inventory</span>
                  <span className="value">{c.inventory_pct}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
